import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Form,
  Row,
  Input,
  Label,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import swal from "sweetalert";
import { useNavigate } from "react-router-dom";

import { ProductListView, _Get } from "../import/ApiEndPoint/ApiCalling";
import { All_Users_List } from "../import/ApiEndPoint/Api";
import { baseURL } from "../import/ApiEndPoint/TestApi";

/* ------------------ helpers ------------------ */
const getUserDisplayName = (u) => {
  const fn = u?.firstName || u?.name || "";
  const ln = u?.lastName || "";
  return `${fn} ${ln}`.trim() || "—";
};

// FY helpers
const FY_MONTHS = [
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
  "January",
  "February",
  "March",
];
const currentFYLabel = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth(); // 0=Jan
  const start = m >= 3 ? y : y - 1; // FY starts in April (3)
  const endShort = String((start + 1) % 100).padStart(2, "0");
  return `${start}-${endShort}`; // e.g., "2025-26"
};
const monthYearForFY = (monthName, fyLabel) => {
  const startYear = parseInt(String(fyLabel).split("-")[0], 10);
  if (!Number.isFinite(startYear)) return "";
  const endYear = startYear + 1;
  const lastQuarter = ["January", "February", "March"];
  const year = lastQuarter.includes(monthName) ? endYear : startYear;
  return `${monthName}-${year}`;
};

const CreateSalesPersonTarget = () => {
  const navigate = useNavigate();

  const [Loading, setLoading] = useState(false);
  const [ProductList, setProductList] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [selectedSalesPersonId, setSelectedSalesPersonId] = useState("");

  // FY-wise month selection
  const [financialYear, setFinancialYear] = useState(currentFYLabel());
  const [monthName, setMonthName] = useState("");

  const [incrementPercent, setIncrementPercent] = useState(10);

  // file upload modal
  const [modalOpen, setModalOpen] = useState(false);
  const [file, setFile] = useState(null);

  // row-wise target & achieved maps
  const [targetQtyMap, setTargetQtyMap] = useState({});
  const [achievedMap, setAchievedMap] = useState({}); // currently 0 for fresh targets

  const userData = useMemo(
    () => JSON.parse(localStorage.getItem("userData") || "{}"),
    []
  );

  /* ---------- Bootstrap lists ---------- */
  useEffect(() => {
    if (!userData?._id || !userData?.database) return;

    // Products
    ProductListView(userData._id, userData.database)
      .then((res) => {
        const list = res?.Product || [];
        setProductList(list);

        const draftAchieved = {};
        list.forEach((p) => {
          if (p?._id) draftAchieved[p._id] = 0;
        });
        setAchievedMap(draftAchieved);
      })
      .catch(() => {});

    // All users (sales persons)
    _Get(All_Users_List, userData?.database)
      .then((res) => {
        const list =
          res?.User || res?.users || res?.adminDetails || res?.data || [];
        const activeSales = (Array.isArray(list) ? list : []).filter(
          (u) =>
            u?.rolename?.roleName === "Sales Person" && u?.status !== "Deactive"
        );
        setSalesPersons(activeSales);
      })
      .catch(() => {});
  }, [userData]);

  /* ---------- Derived rows & totals ---------- */
  const rows = useMemo(() => {
    return ProductList.map((p) => {
      const price = Number(p?.SalesRate || 0);
      const targetQty = Number(targetQtyMap[p._id] || 0);
      const achieved = Number(achievedMap[p._id] || 0);
      const shortfall = Math.max(targetQty - achieved, 0);
      const total = targetQty * price;
      return {
        _id: p._id,
        sId: p.sId,
        name: p?.Product_Title || "—",
        price,
        achieved,
        targetQty,
        shortfall,
        total,
      };
    });
  }, [ProductList, targetQtyMap, achievedMap]);

  const grandTotal = useMemo(
    () =>
      rows.reduce(
        (sum, r) => sum + (Number.isFinite(r.total) ? r.total : 0),
        0
      ),
    [rows]
  );

  const setTargetQty = (productId, value) => {
    const v = Math.max(Number(value || 0), 0);
    setTargetQtyMap((m) => ({ ...m, [productId]: v }));
  };

  // Upload (Excel) – currently just info
  const handleExcelUpload = () => {
    if (!file) return swal("Please upload a file");
    swal(
      "Info",
      "Bulk upload for Sales Person Target is not implemented in this sample. Please fill targets manually.",
      "info"
    );
  };

  // Download simple template: Sales Person × Product
  const handleDownload = async () => {
    try {
      if (!userData?._id || !userData?.database) return;

      const monthLabel = monthName
        ? monthYearForFY(monthName, financialYear)
        : "";

      const data = [];
      salesPersons.forEach((sp) => {
        ProductList.forEach((prod) => {
          data.push({
            salesPersonId: sp?.sId || sp?._id,
            salesPersonName: getUserDisplayName(sp),
            productId: prod?.sId || prod?._id,
            productName: prod?.Product_Title || prod?.ProductTitle || "",
            qtyAssign: "",
            price: prod?.SalesRate || 0,
            month: monthLabel,
            financialYear,
          });
        });
      });

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "SalesPersonTargets");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([buf], { type: "application/octet-stream" }),
        "salesperson_targets_template.xlsx"
      );
    } catch (err) {
      console.log(err);
      swal("Download failed", "Unable to build the template.", "error");
    }
  };

  /* ---------- Submit (save Sales Person Target) ---------- */
  const submitHandler = async (e) => {
    e.preventDefault();
    if (!selectedSalesPersonId)
      return swal("Error", "Choose Sales Person", "error");
    if (!monthName) return swal("Error", "Choose Target Month", "error");
    if (!financialYear) return swal("Error", "Choose Financial Year", "error");

    const sp = salesPersons.find(
      (u) => String(u._id) === String(selectedSalesPersonId)
    );

    const products = rows
      .filter((r) => Number(r.targetQty) > 0)
      .map((r) => ({
        productId: r._id,
        qtyAssign: Number(r.targetQty),
        price: Number(r.price),
        totalPrice: Number((r.price * r.targetQty).toFixed(2)),
        assignPercentage: [],
      }));

    if (!products.length) {
      return swal(
        "Error",
        "Please enter target quantity for at least one product",
        "error"
      );
    }

    const payload = {
      monthName, // e.g., "April"
      financialYear, // e.g., "2025-26"
      date: monthYearForFY(monthName, financialYear), // e.g., "April-2026"

      salesPersonId: selectedSalesPersonId,
      salesPersonName: sp ? getUserDisplayName(sp) : "—",

      products,
      created_by: userData?._id,
      database: userData?.database,

      incrementPercent: Number(incrementPercent) || 0,
      grandTotal: Number(grandTotal.toFixed(2)),
    };

    try {
      setLoading(true);
      const res = await fetch(`${baseURL}${SalesPersonTarget_Save}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      setLoading(false);

      if (data?.status) {
        swal("Target Created Successfully");
        navigate(-1);
      } else {
        swal("Error", data?.message || "Failed to save target", "error");
      }
    } catch (err) {
      setLoading(false);
      console.log(err);
      swal("Error", "Failed to save target", "error");
    }
  };

  // Build FY list (current FY ± 2)
  const fyOptions = useMemo(() => {
    const cur = parseInt(currentFYLabel().split("-")[0], 10);
    const arr = [];
    for (let y = cur - 2; y <= cur + 2; y++) {
      const endShort = String((y + 1) % 100).padStart(2, "0");
      arr.push(`${y}-${endShort}`);
    }
    return arr.reverse();
  }, []);

  // NOTE: import the constant from Api.js (or hardcode string here if you prefer)
  const SalesPersonTarget_Save = "/salesperson-target/save";

  return (
    <div>
      <Card>
        <Row className="m-2">
          <Col>
            <h1>Create Sales Person Target</h1>
          </Col>
          <Col className="d-flex justify-content-end" style={{ gap: 8 }}>
            <Button color="secondary" onClick={() => navigate(-1)}>
              Back
            </Button>
            <Button color="primary" onClick={() => setModalOpen(true)}>
              Upload
            </Button>
            <Button color="primary" onClick={handleDownload}>
              Download
            </Button>
          </Col>
        </Row>

        <CardBody>
          <Form className="m-1" onSubmit={submitHandler}>
            <Row>
              {/* Sales Person dropdown */}
              <Col className="mb-1" lg="3" md="4" sm="12">
                <Label>Sales Person</Label>
                <Input
                  required
                  type="select"
                  value={selectedSalesPersonId}
                  onChange={(e) => setSelectedSalesPersonId(e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {salesPersons.map((sp) => (
                    <option key={sp?._id} value={sp?._id}>
                      {getUserDisplayName(sp)}
                    </option>
                  ))}
                </Input>
              </Col>

              {/* Financial Year */}
              <Col className="mb-1" lg="3" md="4" sm="12">
                <Label>Financial Year</Label>
                <Input
                  type="select"
                  value={financialYear}
                  onChange={(e) => setFinancialYear(e.target.value)}
                >
                  <option value="">Select FY</option>
                  {fyOptions.map((fy) => (
                    <option key={fy} value={fy}>
                      {fy}
                    </option>
                  ))}
                </Input>
              </Col>

              {/* Month (FY order) */}
              <Col className="mb-1" lg="3" md="4" sm="12">
                <Label>Target Month (FY)</Label>
                <Input
                  type="select"
                  value={monthName}
                  onChange={(e) => setMonthName(e.target.value)}
                >
                  <option value="">Select Month</option>
                  {FY_MONTHS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Input>
              </Col>

              {/* Increment % */}
              <Col className="mb-1" lg="3" md="4" sm="12">
                <Label>Enter Increment %</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="e.g. 10"
                  value={incrementPercent}
                  onChange={(e) => setIncrementPercent(e.target.value)}
                />
              </Col>
            </Row>

            {/* ROW-WISE PRODUCTS */}
            <div className="table-responsive mt-2">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th style={{ minWidth: 220 }}>Product</th>
                    <th>Achieved Qty</th>
                    <th>Target Qty</th>
                    <th>Shortfall Qty</th>
                    <th>Price (SalesRate)</th>
                    <th>Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r._id}>
                      <td>{r.name}</td>
                      <td>
                        <Input type="number" readOnly value={r.achieved} />
                      </td>
                      <td>
                        <Input
                          type="number"
                          min="0"
                          value={r.targetQty}
                          onChange={(e) => setTargetQty(r._id, e.target.value)}
                        />
                      </td>
                      <td>
                        <Input type="number" readOnly value={r.shortfall} />
                      </td>
                      <td>
                        <Input type="number" readOnly value={r.price} />
                      </td>
                      <td>
                        <Input
                          type="number"
                          readOnly
                          value={r.total.toFixed(2)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={5} className="text-right">
                      Grand Total
                    </th>
                    <th>
                      <Input
                        type="number"
                        readOnly
                        value={grandTotal.toFixed(2)}
                      />
                    </th>
                  </tr>
                </tfoot>
              </table>
            </div>

            <Row className="mt-2">
              <Col className="d-flex justify-content-center">
                <Button color="primary" type="submit" disabled={Loading}>
                  {Loading ? "Loading..." : "Submit"}
                </Button>
              </Col>
            </Row>
          </Form>
        </CardBody>
      </Card>

      {/* Upload modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
        <ModalHeader toggle={() => setModalOpen(false)}>
          Upload Excel File
        </ModalHeader>
        <ModalBody>
          <Input
            type="file"
            accept=".xlsx, .xls"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={handleExcelUpload}
            disabled={Loading}
          >
            {Loading ? "Uploading..." : "Submit"}
          </Button>
          <Button color="secondary" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CreateSalesPersonTarget;
